import { PieChart } from '@mui/x-charts/PieChart'
import { Paper, Typography, CircularProgress, Box, Card, CardContent, Grid } from '@mui/material'
import IconMoon from 'components/iconmoon'
import { useGetSegmentAnalysisQuery } from 'store/dashboard'
import ChartTitle from './ChartTitle'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'

const CHART_COLORS = [
  '#F0A500',
  '#7B68EE',
  '#20B2AA',
  '#FF6B6B',
  '#4A90D9',
  '#FFD93D',
  '#6BCB77',
  '#4D96FF',
  '#FF8C00',
  '#002C5F'
]

const paperSx = {
  p: 2,
  height: '100%',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  boxShadow: 'none'
}

const SegmentAnalysisChart = () => {
  const { startDate, endDate } = useTimeRange()
  const { data, isLoading, isError, refetch, isFetching } = useGetSegmentAnalysisQuery({
    startDate,
    endDate
  })
  const categories = data?.categories || []

  return (
    <Paper sx={paperSx} elevation={0}>
      <ChartTitle tooltip='The distribution of users across voucher segments based on their interests and behaviors'>
        Segment Analysis
      </ChartTitle>
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <NoDataAvailable variant='error' onRefresh={refetch} />
      ) : !data ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : categories.length === 0 ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <Box
              sx={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'auto' }}
            >
              <PieChart
                series={[
                  {
                    data: categories.map((c, i) => ({
                      id: i,
                      value: c.count,
                      label: c.name,
                      color: CHART_COLORS[i % CHART_COLORS.length]
                    })),
                    innerRadius: 70,
                    outerRadius: 100,
                    cx: 110,
                    highlightScope: { faded: 'global', highlighted: 'item' }
                  }
                ]}
                width={220}
                height={220}
                slotProps={{ legend: { hidden: true } }}
                sx={{ flexShrink: 0 }}
              />
            </Box>
          </Grid>

          <Grid container item xs={12} sm={7} spacing={2} alignContent='flex-start'>
            {categories.map((c, i) => (
              <Grid item key={c.name} xs={6}>
                <Card
                  sx={{
                    bgcolor: '#f8f9fa',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    borderRadius: 2
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <IconMoon
                        icon={c.icon}
                        size={22}
                        color={CHART_COLORS[i % CHART_COLORS.length]}
                      />
                    </Box>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 600 }}
                    >
                      {c.name}
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 700,
                        color: CHART_COLORS[i % CHART_COLORS.length],
                        fontSize: 18
                      }}
                    >
                      {c.percentage}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </Paper>
  )
}

export default SegmentAnalysisChart
